import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ConnectedAccount {
  id: string;
  platform: string;
  account_username: string;
  is_active: boolean;
  created_at: string;
  access_token?: string;
  refresh_token?: string;
  token_expires_at?: string;
  oauth_scope?: string[];
}

export const useConnectedAccounts = () => {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<ConnectedAccount[]>([]);
  const [loading, setLoading] = useState(true);

  const loadAccounts = async () => {
    if (!user) {
      console.log('🚫 No user, clearing accounts');
      setAccounts([]);
      setLoading(false);
      return;
    }

    try {
      console.log('📱 Loading connected accounts for user:', user.id);
      console.log('📱 Current user object:', user);
      
      const { data, error } = await supabase
        .from('social_accounts')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      console.log('📊 Database query result:', { data, error, userIdUsed: user.id });

      if (error) {
        console.error('❌ Error loading accounts:', error);
        throw error;
      }

      console.log('📊 ALL accounts from database (including inactive):', data);

      // Look for specific accounts by username
      const flyBlueBirdie = data?.find(acc => acc.account_username?.includes('flybluebirdie'));
      if (flyBlueBirdie) {
        console.log('🔎 Found flybluebirdie account in raw data:', flyBlueBirdie);
        // Ensure it's active and visible
        if (!flyBlueBirdie.is_active) {
          console.log('🛠️ Fixing inactive flybluebirdie account');
          try {
            const { error } = await supabase
              .from('social_accounts')
              .update({ is_active: true })
              .eq('id', flyBlueBirdie.id);
            
            if (error) {
              console.error('❌ Error activating account:', error);
            } else {
              console.log('✅ Successfully activated flybluebirdie account');
            }
          } catch (e) {
            console.error('❌ Error during account activation:', e);
          }
        }
      } else {
        console.log('🔎 No flybluebirdie account found in raw data');
      }

      console.log('📊 Raw accounts from database:', data);
      
      // Process accounts and clean up any invalid ones
      const processedAccounts = (data || []).filter(account => {
        console.log(`🔍 Processing account:`, {
          id: account.id,
          platform: account.platform,
          username: account.account_username,
          isActive: account.is_active,
          tokenExpiresAt: account.token_expires_at,
          createdAt: account.created_at
        });
        
        // Only include active accounts
        if (!account.is_active) {
          console.log(`❌ Filtering out inactive account: ${account.id}`);
           // Auto-cleanup inactive temp accounts
           if (account.account_username && account.account_username.startsWith('temp_')) {
             console.log(`🧹 Auto-cleaning temporary account: ${account.id}`);
             (async () => {
               try {
                 await supabase
                   .from('social_accounts')
                   .delete()
                   .eq('id', account.id);
                 console.log(`✅ Cleaned up temp account: ${account.id}`);
               } catch (err) {
                 console.error(`❌ Failed to cleanup temp account:`, err);
               }
             })();
           }
          return false;
        }
        
        // Check if account has a valid username
        if (!account.account_username || 
            account.account_username.trim() === '' ||
            account.account_username === 'placeholder') {
          console.log(`❌ Filtering out invalid account: ${account.id}`);
          return false;
        }

        // Check if token is expired
        if (account.token_expires_at) {
          const expiresAt = new Date(account.token_expires_at);
          const now = new Date();
          if (expiresAt <= now) {
            console.log(`❌ Token expired for ${account.platform} account:`, {
              platform: account.platform,
              username: account.account_username,
              expiresAt: expiresAt.toISOString(),
              now: now.toISOString()
            });
            return false; // Token is expired - don't show as connected
          }
        }
        
        // Keep the account
        return true;
      });

      console.log('✅ Final processed accounts:', processedAccounts);
      console.log('✅ Platform breakdown:', processedAccounts.reduce((acc, account) => {
        acc[account.platform] = (acc[account.platform] || 0) + 1;
        return acc;
      }, {} as Record<string, number>));
      setAccounts(processedAccounts);
      
    } catch (error) {
      console.error('💥 Failed to load connected accounts:', error);
      toast.error('Failed to load connected accounts');
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const disconnectAccount = async (accountId: string, platform: string) => {
    try {
      console.log(`🔌 Disconnecting ${platform} account:`, accountId);
      
      const { error } = await supabase
        .from('social_accounts')
        .delete()
        .eq('id', accountId)
        .eq('user_id', user?.id);

      if (error) throw error;

      const platformName = platform === 'twitter' ? 'X (Twitter)' : 
                          platform === 'tiktok' ? 'TikTok' :
                          platform.charAt(0).toUpperCase() + platform.slice(1);
      
      toast.success(`${platformName} account disconnected successfully`);
      
      // Immediate UI update
      setAccounts(prev => prev.filter(acc => acc.id !== accountId));
      
    } catch (error) {
      console.error('❌ Error disconnecting account:', error);
      toast.error('Failed to disconnect account');
    }
  };

  const refreshAccounts = () => {
    console.log('🔄 Manual refresh of connected accounts');
    setLoading(true);
    loadAccounts();
  };

  const hasActiveTwitterConnection = () => {
    const twitterAccounts = accounts.filter(account => 
      account.platform === 'twitter' && 
      account.is_active
    );
    
    console.log('🐦 Twitter connection check:', {
      found: twitterAccounts.length > 0,
      accounts: twitterAccounts,
      usernames: twitterAccounts.map(acc => acc.account_username),
      totalAccounts: accounts.length,
      allAccountPlatforms: accounts.map(acc => acc.platform)
    });
    
    return twitterAccounts.length > 0;
  };
  
  const getTwitterAccounts = () => {
    return accounts.filter(account => 
      account.platform === 'twitter' && 
      account.is_active
    );
  };

  const hasActiveTikTokConnection = () => {
    const tiktokAccounts = accounts.filter(account => 
      account.platform === 'tiktok' && 
      account.is_active
    );
    
    console.log('🎵 TikTok connection check:', {
      found: tiktokAccounts.length > 0,
      accounts: tiktokAccounts,
      usernames: tiktokAccounts.map(acc => acc.account_username)
    });
    
    return tiktokAccounts.length > 0;
  };
  
  const getPlatformAccounts = (platform: string) => {
    return accounts.filter(account => 
      account.platform === platform && 
      account.is_active
    );
  };

  useEffect(() => {
    console.log('👤 User state changed:', { 
      hasUser: !!user, 
      userEmail: user?.email 
    });
    
    if (user) {
      console.log('👤 User found, loading accounts for:', user.email);
      loadAccounts();
    } else {
      console.log('👤 No user, clearing accounts');
      setAccounts([]);
      setLoading(false);
    }
  }, [user]);

  // Real-time updates - separate effect to avoid dependency issues
  useEffect(() => {
    if (!user) return;

    console.log('🔄 Setting up real-time subscription for social_accounts');
    
    try {
      const channelName = `social_accounts_${user.id}`;
      const channel = supabase
        .channel(channelName) // Consistent channel name per user
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'social_accounts',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            console.log('📡 Real-time update received:', payload);
            try {
              setTimeout(() => {
                console.log('📡 Triggering accounts reload after real-time update');
                loadAccounts();
              }, 1000);
            } catch (error) {
              console.error('❌ Error in real-time callback:', error);
            }
          }
        )
        .subscribe((status) => {
          console.log('📡 Subscription status:', status);
        });

      return () => {
        try {
          console.log('🔄 Cleaning up real-time subscription');
          supabase.removeChannel(channel);
        } catch (error) {
          console.error('❌ Error cleaning up subscription:', error);
        }
      };
    } catch (error) {
      console.error('❌ Error setting up real-time subscription:', error);
      // Continue without real-time updates if there's an error
    }
  }, [user?.id]); // Only depend on user ID

  // Listen for forced refresh events (from OAuth completion)
  useEffect(() => {
    const handleForceRefresh = () => {
      console.log('🔄 Force refreshing connected accounts from OAuth completion...');
      loadAccounts();
    };

    window.addEventListener('force-refresh-connections', handleForceRefresh);
    return () => window.removeEventListener('force-refresh-connections', handleForceRefresh);
  }, []);

  return {
    accounts,
    loading,
    loadAccounts,
    disconnectAccount,
    refreshAccounts,
    hasActiveTwitterConnection,
    hasActiveTikTokConnection,
    getTwitterAccounts,
    getPlatformAccounts
  };
};
